import { DropdownOpenHeight } from '@allesauto/utils/consts';
import clsx from 'clsx';
import React, { useCallback } from 'react';

import InfoIconBox from '../infoIconBox';
import SearchDropdown, {
  type Option,
  SearchDropdownProps,
} from '../searchDropdown';

export type Range = {
  minValue: Option | null;
  maxValue: Option | null;
};

export interface DropdownRangeProps extends SearchDropdownProps {
  onChangeRange: (value: Range) => void;
  id?: string;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  rangeValues?: Range;
  minOptions: Option[];
  maxOptions: Option[];
  isSearchable?: boolean;
  openHeight?: DropdownOpenHeight;
}

const DropdownRange = ({
  id = '',
  minOptions,
  maxOptions,
  darkBg,
  hasGreyBg,
  disabled,
  fieldRequired,
  headline,
  infoText = '',
  isSearchable = false,
  minLabel,
  maxLabel,
  minPlaceholder,
  maxPlaceholder,
  rangeValues,
  hasInfoIcon,
  context = '',
  openHeight = DropdownOpenHeight.MAX,
  onChangeRange,
}: DropdownRangeProps) => {
  const handleOnChangeRange = useCallback(
    (value: Option, type: string) => {
      if (type === 'min') {
        onChangeRange({
          minValue: value,
          maxValue: rangeValues?.maxValue || null,
        });
      } else {
        onChangeRange({
          maxValue: value,
          minValue: rangeValues?.minValue || null,
        });
      }
    },
    [rangeValues?.minValue, rangeValues?.maxValue, onChangeRange]
  );

  let minOptionsValues: Option[] = [];
  if (
    rangeValues?.maxValue &&
    typeof rangeValues?.maxValue.value === 'number'
  ) {
    minOptionsValues = minOptions.filter(
      (x) => rangeValues?.maxValue && x.value <= rangeValues?.maxValue.value
    );
  } else {
    minOptionsValues = minOptions;
  }

  let maxOptionsValues: Option[] = [];
  if (rangeValues?.minValue && typeof rangeValues.minValue.value === 'number') {
    maxOptionsValues = maxOptions.filter(
      (x) => rangeValues.minValue && x.value >= rangeValues.minValue.value
    );
  } else {
    maxOptionsValues = maxOptions;
  }

  return (
    <>
      {headline && (
        <div className="flex flex-none items-center mb-5px">
          <div
            className={clsx(
              hasInfoIcon && 'mr-[11px]',
              disabled && darkBg && 'text-white',
              darkBg && 'text-white',
              !darkBg && 'text-grey80'
            )}
          >
            {headline}
          </div>
          {hasInfoIcon && <InfoIconBox text={infoText} darkBg={darkBg} />}
        </div>
      )}

      <div className="flex">
        <SearchDropdown
          id={`${id}-min`}
          context={`range-dropdown--min ${context}`}
          options={minOptionsValues}
          darkBg={darkBg}
          hasGreyBg={hasGreyBg}
          disabled={disabled}
          label={minLabel}
          placeholder={minPlaceholder}
          fieldRequired={fieldRequired}
          isSearchable={isSearchable}
          openHeight={openHeight}
          value={rangeValues?.minValue || null}
          onChange={(x) => handleOnChangeRange(x as Option, 'min')}
        />
        <SearchDropdown
          id={`${id}-max`}
          context={`range-dropdown--max ${context}`}
          options={maxOptionsValues}
          darkBg={darkBg}
          hasGreyBg={hasGreyBg}
          disabled={disabled}
          label={maxLabel}
          placeholder={maxPlaceholder}
          isSearchable={isSearchable}
          openHeight={openHeight}
          fieldRequired={fieldRequired}
          value={rangeValues?.maxValue || null}
          onChange={(x) => handleOnChangeRange(x as Option, 'max')}
        />
      </div>
    </>
  );
};

export default DropdownRange;
